import React from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../store/store';
import WeatherIcon from './WeatherIcon';
import { Wind, Droplet, Thermometer } from 'react-feather';
import LoadingSpinner from '../ui/LoadingSpinner';
import ErrorMessage from '../ui/ErrorMessage';

const WeatherDisplay: React.FC = () => {
  const { t } = useTranslation();
  const { current, loading, error } = useSelector((state: RootState) => state.weather);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-6 bg-white rounded-lg shadow-sm">
        <LoadingSpinner size="md" />
      </div>
    );
  }

  if (error) return <ErrorMessage message={error} />;
  if (!current) return (
    <div className="p-6 bg-white rounded-lg shadow-sm text-center">
      <p className="text-gray-500">{t('weather.noData')}</p>
    </div>
  );

  return (
    <div className="p-6 bg-white rounded-lg shadow-sm hover:shadow-md transition-all duration-300">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between">
        <div className="flex items-center mb-4 md:mb-0">
          <div className="mr-4">
            <WeatherIcon description={current.description} size="lg" />
          </div>
          <div>
            <p className="text-3xl font-bold text-gray-900">{current.temperature}°C</p>
            <p className="text-sm text-gray-600">{current.description}</p>
          </div>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {current.rainfall > 0 && (
            <div className="flex items-center">
              <Droplet className="w-5 h-5 text-blue-500 mr-2" />
              <div>
                <p className="text-xs text-gray-500">{t('weather.rainfall')}</p>
                <p className="text-sm font-medium">{current.rainfall.toFixed(1)} mm</p>
              </div>
            </div>
          )}
          
          <div className="flex items-center">
            <Wind className="w-5 h-5 text-blue-400 mr-2" />
            <div>
              <p className="text-xs text-gray-500">{t('weather.windSpeed')}</p>
              <p className="text-sm font-medium">{current.windSpeed} km/h</p>
            </div>
          </div>
          
          <div className="flex items-center">
            <Thermometer className="w-5 h-5 text-red-500 mr-2" />
            <div>
              <p className="text-xs text-gray-500">{t('weather.temperature')}</p>
              <p className="text-sm font-medium">{current.temperature}°C</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WeatherDisplay;